import { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import useApiHook from "../../hooks/useApiHook";
import { useAuth } from "../../store/auth-context";

const passwordPattern = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,}$/;

const ResetPasswordForm = ({ token, onBackToLogin }) => {
    const authCtx = useAuth();
    const passwordElement = useRef('');
    const confirmPasswordElement = useRef('');
    const { makeRequest } = useApiHook();

    const [status, setStatus] = useState('verifying');
    const [loading, setLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [requestError, setRequestError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            setStatus('verifying');
            const result = await makeRequest('/auth/verify-reset-password-token', 'get', { token });

            if (result?.state === 'success') {
                setStatus('ready');
                return;
            }

            if (result?.error?.code === 410) {
                setStatus('expired');
                return;
            }

            setStatus('invalid');
        };

        verifyToken();
    }, [makeRequest, token]);

    const validatePassword = () => {
        const value = passwordElement.current.value.trim();
        if (!passwordPattern.test(value)) {
            setPasswordError("La password deve contenere almeno una lettera maiuscola, una minuscola, un numero, un simbolo e deve essere lunga almeno 8 caratteri");
            return false;
        }

        setPasswordError('');
        return true;
    };

    const validateConfirmPassword = () => {
        const passwordValue = passwordElement.current.value.trim();
        const confirmValue = confirmPasswordElement.current.value.trim();

        if (!confirmValue || confirmValue !== passwordValue) {
            setConfirmPasswordError("Le password non coincidono");
            return false;
        }

        setConfirmPasswordError('');
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setRequestError('');

        const isPasswordValid = validatePassword();
        const isConfirmPasswordValid = validateConfirmPassword();

        if (!isPasswordValid || !isConfirmPasswordValid) {
            return;
        }

        setLoading(true);

        const result = await makeRequest('/auth/reset-password', 'post', {
            token,
            password: passwordElement.current.value.trim()
        });

        setLoading(false);

        if (result?.state === 'success') {
            setStatus('success');
            await authCtx.completeAuthentication(result.data);
            return;
        }

        if (result?.error?.code === 410) {
            setStatus('expired');
            return;
        }

        setRequestError(result?.error?.details?.password || "Non siamo riusciti ad aggiornare la password. Riprova.");
    };

    const containerStyle = {
        py: 5,
        pb: 3,
        boxSizing: { xs: 'border-box', sm: 'initial' },
        px: { xs: 1, sm: 4 },
        height: { xs: '100dvh', sm: 'auto' },
        width: { xs: '100dvw', sm: 'auto' },
        minWidth: { sm: 400 },
        position: { sm: 'absolute' },
        left: { sm: '50%' },
        top: { sm: '50%' },
        transform: { sm: 'translate(-50%, -50%)' },
    };

    const renderStatusBlock = (currentStatus) => {
        if (currentStatus === 'verifying') {
            return (
                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="h6">Verifica del link in corso...</Typography>
                </Stack>
            );
        }

        if (currentStatus === 'expired') {
            return (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                    <ErrorIcon color="error" fontSize="large" />
                    <Typography color="error" variant="h6" textAlign="center">
                        Il link per reimpostare la password e` scaduto.
                    </Typography>
                    <Button variant="contained" onClick={onBackToLogin}>
                        Torna al login
                    </Button>
                </Stack>
            );
        }

        if (currentStatus === 'invalid') {
            return (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                    <ErrorIcon color="error" fontSize="large" />
                    <Typography color="error" variant="h6" textAlign="center">
                        Il link di reset non e` valido.
                    </Typography>
                    <Button variant="contained" onClick={onBackToLogin}>
                        Torna al login
                    </Button>
                </Stack>
            );
        }

        if (currentStatus === 'success') {
            return (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                    <CheckIcon color="success" fontSize="large" />
                    <Typography color="success.main" variant="h6" textAlign="center">
                        Password aggiornata con successo. Accesso in corso...
                    </Typography>
                </Stack>
            );
        }

        return null;
    };

    return (
        <form onSubmit={handleSubmit}>
            <Card sx={containerStyle}>
                <CardMedia
                    sx={{ borderRadius: 1, mx: 'auto', p: '25px', width: 'auto' }}
                    component="img"
                    alt="Logo Sbobbi"
                    height="150"
                    image="/logo.svg"
                />
                <CardContent sx={{ px: { xs: 0, sm: 2 }, py: 2, textAlign: 'center' }}>
                    {status !== 'ready' ? renderStatusBlock(status) : (
                        <Stack spacing={2}>
                            <Typography variant="h5" fontWeight={700}>
                                Inserisci una nuova password
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Scegli una password sicura e confermala per completare l'accesso.
                            </Typography>
                            <TextField
                                id="reset-password-input"
                                label="Nuova password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                autoComplete="new-password"
                                inputRef={passwordElement}
                                error={Boolean(passwordError)}
                                helperText={passwordError}
                                onBlur={validatePassword}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword((current) => !current)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                id="reset-confirm-password-input"
                                label="Ripeti password"
                                type={showConfirmPassword ? "text" : "password"}
                                variant="outlined"
                                autoComplete="new-password"
                                inputRef={confirmPasswordElement}
                                error={Boolean(confirmPasswordError)}
                                helperText={confirmPasswordError}
                                onBlur={validateConfirmPassword}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={() => setShowConfirmPassword((current) => !current)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Stack>
                    )}
                </CardContent>
                <CardActions sx={{ p: 2, justifyContent: 'center', flexDirection: 'column' }}>
                    {status === 'ready' && requestError ? (
                        <Stack direction="row" mb={3} alignItems="center" spacing={2}>
                            <ErrorIcon color="error" />
                            <Typography variant="body2" color="error" textAlign="left">
                                {requestError}
                            </Typography>
                        </Stack>
                    ) : null}
                    {status === 'ready' ? (
                        <>
                            <Box textAlign="center" width="100%">
                                {!loading ? (
                                    <Button sx={{ height: 50, fontSize: 18 }} type="submit" size="large" variant="contained" fullWidth>
                                        Aggiorna password
                                    </Button>
                                ) : (
                                    <CircularProgress />
                                )}
                            </Box>
                            <Button sx={{ mt: 2 }} onClick={onBackToLogin}>
                                Torna al login
                            </Button>
                        </>
                    ) : null}
                </CardActions>
            </Card>
        </form>
    );
};

export default ResetPasswordForm;
